import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { ImageWordpress } from '../../types/shared';
import { A } from '@solidjs/router';
import Children from './children';

export type LinkSpotProps = {
    variant: 'basic' | 'extended';
    heading: string;
    headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    description: string;
    image: ImageWordpress;
    url?: string;
    target?: string;
    rel?: string;
    hash: string;
};

export const LinkSpot = (props: LinkSpotProps) => {
    const [isShowing, setIsShowing] = createSignal(false);
    const { variant, url, target, rel, hash } = props;
    let observer: IntersectionObserver;

    onMount(() => {
        const el = document.getElementById(hash);
        if (!el) {
            return;
        }

        if (!observer) {
            observer = new IntersectionObserver(([entry]) => {
                if (entry.target.id !== hash) {
                    return;
                }
    
                setIsShowing(entry.isIntersecting);
            });
        }

        observer.observe(el);
    });

    onCleanup(() => observer.disconnect());

    return (
        <Show when={url} fallback={(
            <div
                id={hash}
                class='p-l rounded-l w-full text-center flex flex-col items-center group relative'
                classList={{
                    'gap-m bg-box-white-gradient justify-center aspect-square': variant === 'basic',
                    'px-m gap-s bg-whiteTransparent-10 h-full transition-all delay-100 duration-1000 ease-in-out ': variant === 'extended',
                    'opacity-0 translate-y-10': !isShowing(),
                    'translate-y-30': !isShowing() && variant === 'basic',
                    'opacity-100': isShowing()
                }}
            >
                <Children 
                    variant={variant}
                    heading={props.heading}
                    headingTag={props.headingTag}
                    description={props.description}
                    image={props.image}
                    url={url}
                    target={target}
                    rel={rel}
                />
            </div>
        )}>
            <div
                id={hash}
                class='w-full h-full'
            >
                <A href={url || '/'}
                    class='p-l rounded-l w-full text-center flex flex-col items-center group relative'
                    classList={{
                        'gap-m bg-box-white-gradient justify-center aspect-square': variant === 'basic',
                        'px-m gap-s bg-whiteTransparent-10 h-full transition-all delay-100 duration-1000 ease-in-out': variant === 'extended',
                        'hover:scale-105 focus:scale-105 hover:blur-none focus:blur-none transition-all duration-500 ease-in-out': variant === 'basic' && !!url,
                        'opacity-0 translate-y-10': !isShowing(),
                        'translate-y-30': !isShowing() && variant === 'basic',
                        'opacity-100': isShowing()
                    }}
                    rel={rel}
                    target={target}
                >
                    <Children 
                        variant={variant}
                        heading={props.heading}
                        headingTag={props.headingTag}
                        description={props.description}
                        image={props.image}
                        url={url}
                        target={target}
                        rel={rel}
                    />
                </A>
            </div>
        </Show>
    );
};

LinkSpot.parseProps = (atts: LinkSpotProps) => {
    return {
        description: atts.description,
        variant: atts.variant,
        heading: atts.heading,
        headingTag: atts.headingTag,
        image: atts.image,
        url: atts.url,
        target: atts.target,
        rel: atts.rel,
        hash: atts.hash,
    };
};
